/**
 * The table's materials: walnut for the table, travertine for the board,
 * parchment for the map, water for the harbour, the back of a card, and the
 * soft shadow and glow every piece and target is drawn with.
 *
 * The four surfaces and the card back are original generated images in `art/`
 * (prompts in art/README.md), loaded once by `loadArt` before the first page is
 * drawn. If one fails to load, the seeded-noise painting below stands in for it,
 * so the table always has a surface. Everything is cached, so the second table
 * built on a page costs nothing.
 */
import { Texture } from 'pixi.js';
import cardBackUrl from './art/card-back.webp';
import parchmentUrl from './art/parchment.webp';
import stoneUrl from './art/stone.webp';
import waterUrl from './art/water.webp';
import woodUrl from './art/wood.webp';
import { fbm, scatter, valueNoise } from './noise';
import { PARCHMENT, STONE, WATER, WOOD } from './palette';

type Rgb = [number, number, number];
const rgb = (n: number): Rgb => [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (t: number) => (t < 0 ? 0 : t > 1 ? 1 : t);
const smoothstep = (a: number, b: number, t: number) => {
  const x = clamp01((t - a) / (b - a));
  return x * x * (3 - 2 * x);
};

function canvas(w: number, h: number): [HTMLCanvasElement, CanvasRenderingContext2D] {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  if (!ctx) throw new Error('no 2d canvas context');
  return [c, ctx];
}

/** Fill a square texture pixel by pixel from a shader-like function returning RGB. */
function paint(size: number, shade: (u: number, v: number) => Rgb): HTMLCanvasElement {
  const [c, ctx] = canvas(size, size);
  const img = ctx.createImageData(size, size);
  const d = img.data;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const [r, g, b] = shade(x / size, y / size);
      const i = (y * size + x) * 4;
      d[i] = r;
      d[i + 1] = g;
      d[i + 2] = b;
      d[i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

const blend = (a: Rgb, b: Rgb, t: number): Rgb => [
  lerp(a[0], b[0], t),
  lerp(a[1], b[1], t),
  lerp(a[2], b[2], t),
];
const shadeBy = (c: Rgb, k: number): Rgb => [c[0] * k, c[1] * k, c[2] * k];

// ---------------------------------------------------------------------------
// The generated art
// ---------------------------------------------------------------------------

const ART_URL = {
  wood: woodUrl,
  stone: stoneUrl,
  parchment: parchmentUrl,
  water: waterUrl,
  cardBack: cardBackUrl,
} as const;
type ArtName = keyof typeof ART_URL;
const art = new Map<ArtName, HTMLCanvasElement>();

/**
 * Load the generated art, each image onto a canvas of its own. Resolves once
 * every image has loaded or failed — a failure only means that surface is
 * painted from noise instead — and never rejects. Call it before anything is
 * drawn: a texture asked for earlier is painted from noise and stays that way.
 */
export async function loadArt(): Promise<void> {
  await Promise.all(
    (Object.keys(ART_URL) as ArtName[]).map(async (name) => {
      try {
        const img = new Image();
        img.src = ART_URL[name];
        await img.decode();
        const [c, ctx] = canvas(img.naturalWidth, img.naturalHeight);
        ctx.drawImage(img, 0, 0);
        art.set(name, c);
      } catch {
        // The noise painting stands in for this one.
      }
    }),
  );
}

/** The image a surface is drawn from, when it loaded — for CSS, which can use the file itself. */
export const artUrl = (name: ArtName): string | null => (art.has(name) ? ART_URL[name] : null);

const cache = new Map<string, Texture>();
/** The canvases behind the textures, which the HTML around the table paints with too. */
const painted = new Map<string, HTMLCanvasElement>();
function cached(key: string, make: () => HTMLCanvasElement): Texture {
  let t = cache.get(key);
  if (!t) {
    const c = make();
    painted.set(key, c);
    t = Texture.from(c);
    t.source.style.addressMode = 'repeat';
    cache.set(key, t);
  }
  return t;
}

/** Dark walnut with the grain running along the table. */
export const woodTexture = (): Texture => cached('wood', () => art.get('wood') ?? paintWood());

function paintWood(): HTMLCanvasElement {
  const dark = rgb(WOOD.dark);
  const mid = rgb(WOOD.mid);
  const light = rgb(WOOD.light);
  return paint(512, (u, v) => {
    const streak = fbm(11, u, v, 2, 28, 5);
    const warp = fbm(23, u, v, 3, 5, 3);
    const figure = 0.5 + 0.5 * Math.sin((v * 36 + warp * 5) * Math.PI * 2);
    const t = clamp01(0.62 * streak + 0.38 * figure * figure);
    const base = t < 0.5 ? blend(dark, mid, t * 2) : blend(mid, light, (t - 0.5) * 1.3);
    const fine = valueNoise(5, u * 256, v * 64, 256, 64);
    return shadeBy(base, 0.9 + fine * 0.18);
  });
}

/** Warm travertine: soft horizontal banding, a few veins, and the stone's little pits. */
export const stoneTexture = (): Texture => cached('stone', () => art.get('stone') ?? paintStone());

function paintStone(): HTMLCanvasElement {
  const light = rgb(STONE.light);
  const mid = rgb(STONE.mid);
  const dark = rgb(STONE.dark);
  return paint(512, (u, v) => {
    const band = fbm(31, u, v, 2, 9, 5);
    const cloud = fbm(37, u, v, 4, 4, 4);
    const t = smoothstep(0.3, 0.78, 0.62 * band + 0.38 * cloud);
    let c = blend(light, mid, t);
    const vein = Math.abs(fbm(41, u, v, 3, 3, 4) - 0.5);
    if (vein < 0.016) c = blend(c, dark, (0.016 - vein) * 22);
    const pit = valueNoise(43, u * 128, v * 200, 128, 200);
    if (pit > 0.9) c = blend(c, dark, (pit - 0.9) * 7);
    const grain = valueNoise(47, u * 256, v * 256, 256, 256);
    return shadeBy(c, 0.97 + grain * 0.05);
  });
}

/** Parchment for the map: clouded, with foxing spots and a faint fibre. */
export const parchmentTexture = (): Texture =>
  cached('parchment', () => art.get('parchment') ?? paintParchment());

function paintParchment(): HTMLCanvasElement {
  const light = rgb(PARCHMENT.light);
  const mid = rgb(PARCHMENT.mid);
  const dark = rgb(PARCHMENT.dark);
  return paint(512, (u, v) => {
    const cloud = fbm(53, u, v, 3, 3, 5);
    const blotch = fbm(59, u, v, 7, 7, 3);
    let c = blend(light, mid, smoothstep(0.35, 0.8, 0.7 * cloud + 0.3 * blotch));
    if (blotch > 0.7) c = blend(c, dark, (blotch - 0.7) * 1.6);
    const fibre = valueNoise(61, u * 512, v * 48, 512, 48);
    return shadeBy(c, 0.96 + fibre * 0.06);
  });
}

/** Harbour water: dark teal, with the light catching the swell. */
export const waterTexture = (): Texture => cached('water', () => art.get('water') ?? paintWater());

function paintWater(): HTMLCanvasElement {
  const light = rgb(WATER.light);
  const mid = rgb(WATER.mid);
  const dark = rgb(WATER.dark);
  return paint(256, (u, v) => {
    const w = fbm(67, u, v, 4, 8, 4);
    let c = blend(dark, mid, smoothstep(0.25, 0.75, w));
    const swell = Math.sin((v * 16 + w * 2.4) * Math.PI * 2);
    if (swell > 0.86) c = blend(c, light, (swell - 0.86) * 4);
    return c;
  });
}

/**
 * The back of every card, when its image loaded; `null` means draw the plain
 * red-and-bronze back in code instead (pieces.ts).
 */
export const cardBackTexture = (): Texture | null => {
  const c = art.get('cardBack');
  return c ? cached('cardBack', () => c) : null;
};

/**
 * A soft shadow: a blurred rounded square, to be stretched as a nine-slice
 * under anything rectangular. The core is opaque; the piece on top hides it.
 */
export const SHADOW_SLICE = 30;
export const shadowTexture = (): Texture =>
  cached('shadow', () => {
    const [c, ctx] = canvas(96, 96);
    ctx.shadowColor = 'rgba(0,0,0,1)';
    ctx.shadowBlur = 14;
    ctx.fillStyle = '#000';
    ctx.beginPath();
    ctx.roundRect(30, 30, 36, 36, 8);
    ctx.fill();
    return c;
  });

/** A round shadow for discs and pawns. */
export const dotShadowTexture = (): Texture =>
  cached('dot-shadow', () => {
    const [c, ctx] = canvas(64, 64);
    const g = ctx.createRadialGradient(32, 32, 4, 32, 32, 30);
    g.addColorStop(0, 'rgba(0,0,0,0.75)');
    g.addColorStop(0.5, 'rgba(0,0,0,0.35)');
    g.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    return c;
  });

/**
 * A faint paper grain laid over card and tile faces, white specks and dark
 * specks on transparency, so any face colour shows through it.
 */
export const grainTexture = (): Texture =>
  cached('grain', () => {
    const [c, ctx] = canvas(128, 128);
    const next = scatter(71);
    for (let i = 0; i < 900; i++) {
      const light = next() > 0.5;
      ctx.fillStyle = light ? 'rgba(255,255,255,0.22)' : 'rgba(60,40,20,0.14)';
      ctx.fillRect(next() * 128, next() * 128, 1 + next() * 1.5, 1);
    }
    return c;
  });

/** The surfaces the HTML around the table is laid on, so a page and the table match. */
export type Surface = 'wood' | 'stone' | 'grain';
const SURFACES: Record<Surface, () => Texture> = {
  wood: woodTexture,
  stone: stoneTexture,
  grain: grainTexture,
};

/** The canvas a surface is painted on — the very one the table's texture is made from. */
export function surfaceCanvas(name: Surface): HTMLCanvasElement {
  SURFACES[name]();
  const c = painted.get(name);
  if (!c) throw new Error(`surface ${name} was not painted`);
  return c;
}
